import { returnCalendarForCurrency } from "@/lib/businessRules/calendarBusinessRule";
import { IBondSecurity } from "@/lib/interfaces and Enums/ENUMBondSecurity";
import {
  SecurityIdentifierType,
  TradeMessageSubType,
  TradeMessageType,
} from "@/lib/interfaces and Enums/ENUMBondTradeMessage";
import {
  IBondOptionTradeMessage,
  IOptionSecurityDetails,
} from "@/lib/interfaces and Enums/ENUMOptionTradeMessage";
import { IPortfolio } from "@/lib/interfaces and Enums/ENUMPortfolio";
import { returnSizeFormatted } from "@/lib/globalFormatterHelper";
import { IBondOptionTicket } from "@/store/tradeFromScratchStore";
import {
  ITicketIssue,
  returnAllocationIssues,
  returnWireAllocations,
} from "./allocationRules";
import {
  ITicketField,
  returnIndicativePrice,
  returnRequiredTicketKeys,
  returnTicketFieldIssues,
  returnTransactionNumberDetail,
} from "./ticketSharedHelper";

// LQC-2676 - pure rules for the bond option ticket. No React, no store reads: the caller
// passes the resolved underlying bond and portfolio in, so all of this is unit testable.
// Mirrors bondTicketHelper / futureTicketHelper.

// Picking (or clearing) the underlying. Payment currency and Strike are defaults only;
// Strike takes the bond's latest price (spec F7, LQC-2953).
export function returnBondOptionUnderlyingChanges(
  bond: IBondSecurity | undefined,
  isin: string,
): Partial<IBondOptionTicket> {
  return {
    isin,
    securityName: bond?.securityName ?? "",
    currencyPayment: bond?.currency ?? "",
    strike: returnIndicativePrice(bond),
  };
}

export type IBondOptionTicketField = ITicketField<IBondOptionTicket>;

// Strike is deliberately absent: the API has it optional (Range validated only when
// sent), so the ticket can be sent without one.
export const BOND_OPTION_TICKET_REQUIRED_FIELDS: IBondOptionTicketField[] = [
  { key: "isin", label: "Underlying" },
  { key: "portfolioCode", label: "Portfolio" },
  { key: "optionType", label: "Option Type" },
  { key: "expiryDate", label: "Expiry Date", isDate: true },
  { key: "nominal", label: "Nominal", isNumeric: true },
  { key: "direction", label: "Direction" },
  { key: "settlementDate", label: "Settlement Date", isDate: true },
  { key: "tradeDate", label: "Trade Date", isDate: true },
];

// What the ticket marks with a "*". Derived from the list above so the two cannot
// drift apart.
export const BOND_OPTION_TICKET_REQUIRED_KEYS = returnRequiredTicketKeys(
  BOND_OPTION_TICKET_REQUIRED_FIELDS,
);

export function returnMissingBondOptionTicketEntries(
  ticket: IBondOptionTicket,
): ITicketIssue[] {
  return [
    ...returnTicketFieldIssues(ticket, BOND_OPTION_TICKET_REQUIRED_FIELDS),
    ...returnAllocationIssues(ticket.nominal, ticket.additionalAllocations),
  ];
}

export function returnMissingBondOptionTicketFields(
  ticket: IBondOptionTicket,
): string[] {
  return returnMissingBondOptionTicketEntries(ticket).map(({ label }) => label);
}

export function isBondOptionTicketComplete(ticket: IBondOptionTicket): boolean {
  return returnMissingBondOptionTicketFields(ticket).length === 0;
}

// The option cannot expire before it settles.
export function isExpiryAfterSettlement(ticket: IBondOptionTicket): boolean {
  if (!ticket.expiryDate || !ticket.settlementDate) return true;
  return ticket.expiryDate >= ticket.settlementDate;
}

// An option outliving its underlying is unpriceable: MATLAB's instargbondmod throws
// "Settle must be less than Maturity for all Bonds" and the POST never returns, so the
// block is written but the request hangs. Caught here instead (LQC-2676).
export function isExpiryWithinUnderlyingMaturity(
  ticket: IBondOptionTicket,
  underlying: IBondSecurity | undefined,
): boolean {
  if (!ticket.expiryDate || !underlying?.maturityDate) return true;
  return ticket.expiryDate <= underlying.maturityDate;
}

// Names the field as well as the problem, so the ticket can put the message on the row
// that is wrong. Mirrors returnSwaptionDateIssue.
export interface IBondOptionDateIssue {
  key: "expiryDate";
  message: string;
}

export function returnBondOptionDateIssue(
  ticket: IBondOptionTicket,
  underlying: IBondSecurity | undefined,
): IBondOptionDateIssue | null {
  if (!isExpiryAfterSettlement(ticket)) {
    return {
      key: "expiryDate",
      message: `Must be on or after settlement (${ticket.settlementDate})`,
    };
  }
  if (!isExpiryWithinUnderlyingMaturity(ticket, underlying)) {
    return {
      key: "expiryDate",
      message: `Must be on or before the underlying matures (${underlying?.maturityDate})`,
    };
  }
  return null;
}

// The underlying's own currency. Only the DEFAULT payment currency, not the answer:
// GET /Securities/bondoptions shows 147 of 586 real options paying in a different
// currency from the bond (143 of them USD bonds paying EUR), so the ticket lets the
// user override it.
export function returnBondOptionCurrency(
  underlying: IBondSecurity | undefined,
): string {
  return underlying?.currency ?? "";
}

// What actually gets sent: the user's choice, falling back to the underlying.
export function returnBondOptionPaymentCurrency(
  ticket: IBondOptionTicket,
  underlying: IBondSecurity | undefined,
): string {
  return ticket.currencyPayment || returnBondOptionCurrency(underlying);
}

// Keyed off the PAYMENT currency, not the bond's - the calendar is the settlement
// business centre.
export function returnBondOptionCalendar(
  ticket: IBondOptionTicket,
  underlying: IBondSecurity | undefined,
): string {
  return returnCalendarForCurrency(
    returnBondOptionPaymentCurrency(ticket, underlying),
  );
}

// What the confirmation line describes. Built at send time - the ticket is cleared the
// moment the POST succeeds, so nothing can be read back off it afterwards.
export function returnSentBondOptionTicketSummary(
  ticket: IBondOptionTicket,
): string {
  const nominal =
    typeof ticket.nominal === "number" && ticket.nominal > 0
      ? returnSizeFormatted(ticket.nominal)
      : "";
  const strike =
    typeof ticket.strike === "number" && ticket.strike > 0
      ? `@ ${ticket.strike}`
      : "";
  return [ticket.direction, nominal, ticket.isin, ticket.optionType, strike]
    .filter(Boolean)
    .join(" ");
}

export interface IBuildBondOptionTradeMessageParams {
  ticket: IBondOptionTicket;
  underlying: IBondSecurity | undefined;
  portfolio: IPortfolio | undefined;
  traderName: string;
  // Used only when the portfolio row carries no customerCode.
  fallbackEntity?: string;
}

// Strike is omitted entirely when unset - the API Range check rejects 0, so sending a
// placeholder would fail where sending nothing passes.
function returnSecurityDetails(
  ticket: IBondOptionTicket,
  underlying: IBondSecurity | undefined,
): IOptionSecurityDetails {
  const details: IOptionSecurityDetails = {
    optionType: ticket.optionType as IOptionSecurityDetails["optionType"],
    optionStyle: ticket.optionStyle,
    premiumType: ticket.premiumType,
    delivery: ticket.delivery,
    expiryDate: ticket.expiryDate,
    currencyPayment: returnBondOptionPaymentCurrency(ticket, underlying),
    calendar: returnBondOptionCalendar(ticket, underlying),
  };
  if (typeof ticket.strike === "number" && ticket.strike > 0) {
    details.strike = ticket.strike;
    details.strikeQuotation = ticket.strikeQuotation;
  }
  return details;
}

// Returns undefined when the ticket cannot legally be sent, so the caller never has to
// decide what a half-built message means.
export function buildBondOptionTradeMessage({
  ticket,
  underlying,
  portfolio,
  traderName,
  fallbackEntity = "",
}: IBuildBondOptionTradeMessageParams): IBondOptionTradeMessage | undefined {
  if (!isBondOptionTicketComplete(ticket)) return undefined;
  if (returnBondOptionDateIssue(ticket, underlying)) return undefined;
  if (!portfolio) return undefined;
  // currencyPayment is required and 3 characters, so neither an unresolved bond nor a
  // cleared override can be sent.
  if (!returnBondOptionPaymentCurrency(ticket, underlying)) return undefined;
  const nominal = ticket.nominal as number;

  return {
    messageType: TradeMessageType.Trade,
    subType: TradeMessageSubType.BondOption,
    clientDetails: {
      originatingEntity: portfolio.customerCode || fallbackEntity,
      lei: portfolio.lei,
      traderName,
    },
    securityDetails: returnSecurityDetails(ticket, underlying),
    underlyingSecurity: {
      identifierDetails: [
        {
          identifier: ticket.isin,
          identifierType: SecurityIdentifierType.ISIN,
        },
      ],
    },
    // Must stay empty: the option security does not exist yet, and a non-empty list
    // makes the API demand a UTI / ExternalID / ExternalKey it will never get.
    security: {},
    tradeDetails: {
      tradeDate: ticket.tradeDate,
      direction: ticket.direction,
      nominal,
      settlementDate: ticket.settlementDate,
      openClose: ticket.openClose,
      ...returnTransactionNumberDetail(ticket.transactionNumber),
    },
    allocations: returnWireAllocations(
      portfolio.portfolioCode,
      nominal,
      ticket.additionalAllocations,
    ),
  };
}
